import { Suspense, memo, useEffect } from 'react';

import { lazy } from '@loadable/component';
import { useLocation } from 'react-router';

import { changeAppDrawerTab } from 'actions/appDrawer';
import { CenterLoader } from 'components/loader';
import MobileProductHeader from 'components/productHeaderMobile';
import Render from 'components/render';
import { isFutures } from 'helpers/utils';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { appDrawerSelector } from 'selectors/otherSelectors';
import { selectedContractTypeSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import Box from 'UIKit/Box';

import ChartOrderBookTab from './mobile/ChartOrderbookTab';
import styles from './tradeMobile.module.scss';

const PlaceOrderSection = lazy(() =>
  import(
    /* webpackChunkName: "PlaceOrderSection" */ './trade_components_lazy/placeOrderSection'
  )
);

const Positions = lazy(() =>
  import(/* webpackChunkName: "Positions" */ './trade_components_lazy/positions')
);

const MobileOrderBook = lazy(() =>
  import(/* webpackChunkName: "OrderBookNew" */ 'components/orderbookNew')
);

const MobileFundingCountdown = lazy(() =>
  import(
    /* webpackChunkName: "MobileFundingCountdown" */ './mobile/MobileFundingCountdown'
  )
);

const MobileTradePageSection = () => {
  const selectedContractType = useAppSelector(selectedContractTypeSelector);
  const isSelectedContractTypeIsFutures =
    selectedContractType && isFutures(selectedContractType);

  return (
    <Box flex="flex" className={styles.mobileTradePageSectionContainer}>
      <Box className={styles.mobileOrderBookContainer}>
        <Render when={isSelectedContractTypeIsFutures}>
          <Box className={styles.mobileFundingCountdownContainer}>
            <MobileFundingCountdown />
          </Box>
        </Render>
        <MobileOrderBook />
      </Box>
      <PlaceOrderSection />
    </Box>
  );
};

const TradeMobile = () => {
  const { activeTab } = useAppSelector(appDrawerSelector);
  const location = useLocation();
  const isTradeTab = activeTab === APP_DRAWER_TAB_ID_MAPPING.TRADE;
  const isChartTab = activeTab === APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK;
  const isPositionsTab = activeTab === APP_DRAWER_TAB_ID_MAPPING.POSITIONS;

  const appDispatch = useAppDispatch();
  const isUrlParamsShowScalpingPopup = location?.query?.showScalpingPopup;

  // Since we have 3 tabs with the same URL, we added a query search parameter to redirect to the trade page when the user lands on the page.
  // This query parameter is the same one used to open the scalper offer popup.
  useEffect(() => {
    if (isUrlParamsShowScalpingPopup) {
      appDispatch(changeAppDrawerTab(APP_DRAWER_TAB_ID_MAPPING.TRADE));
    }
  }, [isUrlParamsShowScalpingPopup, appDispatch]);

  return (
    <>
      <Render when={!isPositionsTab}>
        <MobileProductHeader />
      </Render>
      <Suspense fallback={<CenterLoader />}>
        <Render when={isPositionsTab}>
          <Positions />
        </Render>
        <Render when={isChartTab}>
          <ChartOrderBookTab />
        </Render>
        <Render when={isTradeTab}>
          <MobileTradePageSection />
        </Render>
      </Suspense>
    </>
  );
};

export { PlaceOrderSection };
export default memo(TradeMobile);
